import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FleetProvider } from "@/hooks/use-fleet-providers";
import { useFleetProviderTypes } from "@/hooks/use-fleet-provider-types";

interface ProviderFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
  provider?: FleetProvider | null;
  isLoading?: boolean;
  companyId: string;
}

export function ProviderFormSheet({
  open,
  onOpenChange,
  onSubmit,
  provider,
  isLoading = false,
  companyId
}: ProviderFormSheetProps) {
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    cnpj: "",
    status: "active",
    contact_phone: "",
    contact_email: "",
    contact_person: "",
    address_street: "",
    address_number: "",
    address_city: "",
    address_state: "",
    address_zip_code: ""
  });

  const [errors, setErrors] = useState<{ type?: string }>({});

  useEffect(() => {
    if (provider) {
      setFormData({
        name: provider.name,
        type: provider.type,
        cnpj: provider.cnpj || "",
        status: provider.status,
        contact_phone: provider.contact_info?.phone || "",
        contact_email: provider.contact_info?.email || "",
        contact_person: provider.contact_info?.contact_person || "",
        address_street: provider.address?.street || "",
        address_number: provider.address?.number || "",
        address_city: provider.address?.city || "",
        address_state: provider.address?.state || "",
        address_zip_code: provider.address?.zip_code || ""
      });
    } else {
      setFormData({
        name: "",
        type: "",
        cnpj: "",
        status: "active",
        contact_phone: "",
        contact_email: "",
        contact_person: "",
        address_street: "",
        address_number: "",
        address_city: "",
        address_state: "",
        address_zip_code: ""
      });
    }
  }, [provider, open]);

  const validateForm = () => {
    const newErrors: { type?: string } = {};
    
    if (!formData.type) {
      newErrors.type = "Por favor, selecione o tipo de fornecedor";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const submitData = {
      name: formData.name,
      type: formData.type,
      cnpj: formData.cnpj || null,
      status: formData.status,
      contact_info: {
        phone: formData.contact_phone || null,
        email: formData.contact_email || null,
        contact_person: formData.contact_person || null
      },
      address: {
        street: formData.address_street || null,
        number: formData.address_number || null,
        city: formData.address_city || null,
        state: formData.address_state || null,
        zip_code: formData.address_zip_code || null,
        country: "Brasil"
      }
    };

    if (provider) {
      onSubmit({ id: provider.id, ...submitData });
    } else {
      onSubmit(submitData);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const { data: providerTypesFromDb = [] } = useFleetProviderTypes(companyId);
  
  // Filter active types and sort alphabetically
  const providerTypes = providerTypesFromDb
    .filter(type => type.is_active)
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    .map(type => ({
      value: type.code,
      label: type.name
    }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[650px] p-6 overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle>
            {provider ? "Editar Fornecedor" : "Novo Fornecedor"}
          </SheetTitle>
          <SheetDescription>
            Cadastre ou edite informações do fornecedor de serviços.
          </SheetDescription>
        </SheetHeader>
        
        <ScrollArea className="flex-1 -mx-6 px-6">
          <form id="provider-form" onSubmit={handleSubmit} className="space-y-6 py-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium border-b pb-2">Informações Básicas</h4>
              
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Nome do fornecedor"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="type">Tipo *</Label>
                  <Select 
                    value={formData.type} 
                    onValueChange={(value) => {
                      handleChange("type", value);
                      setErrors(prev => ({ ...prev, type: undefined }));
                    }}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {providerTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.type && (
                    <p className="text-sm text-destructive">{errors.type}</p>
                  )}
                </div>

              <div className="grid gap-4 grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <Input
                    id="cnpj"
                    value={formData.cnpj}
                    onChange={(e) => handleChange("cnpj", e.target.value)}
                    placeholder="00.000.000/0000-00"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => handleChange("status", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="inactive">Inativo</SelectItem>
                      <SelectItem value="blacklisted">Lista Negra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium border-b pb-2">Informações de Contato</h4>
              
              <div className="space-y-2">
                <Label htmlFor="contact_person">Pessoa de Contato</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => handleChange("contact_person", e.target.value)}
                  placeholder="Nome do responsável"
                />
              </div>

            <div className="grid gap-4 grid-cols-2">
              <div className="space-y-2">
                  <Label htmlFor="contact_phone">Telefone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange("contact_phone", e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email">Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleChange("contact_email", e.target.value)}
                    placeholder="contato@fornecedor.com"
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium border-b pb-2">Endereço</h4>
              
              <div className="space-y-2">
                <Label htmlFor="address_street">Logradouro</Label>
                <Input
                  id="address_street"
                  value={formData.address_street}
                  onChange={(e) => handleChange("address_street", e.target.value)}
                  placeholder="Rua, Avenida, etc."
                />
              </div>

            <div className="grid gap-4 grid-cols-[30%_70%]">
              <div className="space-y-2">
                <Label htmlFor="address_number">Número</Label>
                  <Input
                    id="address_number"
                    value={formData.address_number}
                    onChange={(e) => handleChange("address_number", e.target.value)}
                  placeholder="123"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_zip_code">CEP</Label>
                <Input
                  id="address_zip_code"
                  value={formData.address_zip_code}
                  onChange={(e) => handleChange("address_zip_code", e.target.value)}
                  placeholder="00000-000"
                />
              </div>
            </div>

            <div className="grid gap-4 grid-cols-[70%_30%]">
                <div className="space-y-2">
                  <Label htmlFor="address_city">Cidade</Label>
                  <Input
                    id="address_city"
                    value={formData.address_city}
                    onChange={(e) => handleChange("address_city", e.target.value)}
                    placeholder="São Paulo"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address_state">Estado</Label>
                  <Input
                    id="address_state"
                    value={formData.address_state}
                    onChange={(e) => handleChange("address_state", e.target.value)}
                    placeholder="SP"
                    maxLength={2}
                  />
                </div>
              </div>
            </div>
          </form>
        </ScrollArea>

        <SheetFooter className="border-t pt-4 mt-auto">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="provider-form" disabled={isLoading || !formData.type}>
            {isLoading ? "Salvando..." : provider ? "Atualizar" : "Criar"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
